import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';
import { DGPTAlertEventInstance } from 'types/dgptAlerts';

export const keys = {
  alertEventInstances: 'alertEventInstances',
} as const;

interface UpdateAlertEventInstancePayload {
  id: string;
  configId?: string;
  updates: Partial<DGPTAlertEventInstance>;
}

export const useAlertEventInstances = (configId?: string) => {
  const groupGuid = useSelector(selectGroupGuid);

  const queryOptions: UseQueryOptions<DGPTAlertEventInstance[], Error> = {
    queryKey: [keys.alertEventInstances, groupGuid, configId],
    queryFn: async () => {
      const params: { groupGuid: string; configId?: string } = { groupGuid };
      if (configId) params.configId = configId;

      const response = await axios.get(
        `${config.apiGateway.dgptAlerts}/alert-event-instances`,
        { params }
      );
      return response.data;
    },
  };

  return useQuery<DGPTAlertEventInstance[], Error>(queryOptions);
};

// Get an alert event instance by ID
export const useAlertEventInstanceById = (id?: string) => {
  const queryOptions: UseQueryOptions<DGPTAlertEventInstance | null, Error> = {
    queryKey: [keys.alertEventInstances, 'detail', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await axios.get(
        `${config.apiGateway.dgptAlerts}/alert-event-instances/${id}`
      );
      return response.data;
    },
    enabled: !!id,
  };

  return useQuery<DGPTAlertEventInstance | null, Error>(queryOptions);
};

export const useUpdateAlertEventInstance = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { response: DGPTAlertEventInstance; configId?: string },
    Error,
    UpdateAlertEventInstancePayload
  >({
    mutationFn: async ({ id, configId, updates }) => {
      const response = await axios.put(
        `${config.apiGateway.dgptAlerts}/alert-event-instances/${id}`,
        updates
      );
      return { response: response.data, configId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [keys.alertEventInstances] });
      if (data.configId) {
        queryClient.invalidateQueries({
          queryKey: [keys.alertEventInstances, undefined, data.configId],
        });
      }
      toast.success('Alert event instance updated successfully.');
    },
    onError: (error) => {
      console.error('Error updating alert event instance:', error);
      toast.error('Failed to update alert event instance.');
    },
  });
};
